import { useAuth } from "@lib/auth/useAuth";
import { useEffect, useRef } from "react";
import { useStore } from "@store/index";
import { useGetMyActiveData } from "@lib/commerce/hooks/cartOperations";
import { useCart } from "@hooks/useCart";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { useQuery } from "@tanstack/react-query";
import { QueryKeys } from "@lib/enums/QueryKeys";

export const useFreshData = () => {
    const setHasFreshData = useStore((state) => state.setHasFreshData);
    const clearMe = useStore((state) => state.clearMe);
    const clearWishlist = useStore((state) => state.clearWishlist);
    const { session, status } = useAuth();
    const { recalculateCart } = useCart();

    const executeGetMyActiveData = useGetMyActiveData();

    const ucid = session?.ucid ?? null;
    const anonymousId = session?.anonymousId ?? null;

    // updateQuery auto-applies the response to zustand before we can intercept,
    // so we re-check identity after the await and clear if it drifted. Assigned
    // during render (not in useEffect) so the queryFn continuation always sees
    // the latest committed session before passive effects flush.
    const sessionRef = useRef(session);
    sessionRef.current = session;

    const { isSuccess } = useQuery({
        queryKey: [QueryKeys.getMyActiveCartData, ucid, anonymousId],
        queryFn: async () => {
            const fetchUcid = ucid;
            const fetchAnonId = anonymousId;
            const hasIdentityDrifted = () => {
                const currentUcid = sessionRef.current?.ucid ?? null;
                const currentAnonId = sessionRef.current?.anonymousId ?? null;
                return currentUcid !== fetchUcid || currentAnonId !== fetchAnonId;
            };
            const discardOnDrift = () => {
                clearMe();
                clearWishlist();
                throw new Error("ACTIVE_DATA_IDENTITY_DRIFT");
            };

            const activeDataResponse = await executeGetMyActiveData();
            if (hasIdentityDrifted()) discardOnDrift();
            if (activeDataResponse?.errors?.length) {
                Logger.error(ServiceType.WEB, "Fetching fresh active data failed", {
                    tag: LogTag.SHOPPING_BAG,
                    errors: activeDataResponse.errors,
                });
                throw new Error("ACTIVE_DATA_FETCH_FAILED");
            }

            return activeDataResponse;
        },
        enabled: status === "authenticated",
        // Cart can drift across tabs/devices; refetch on focus + zero staleTime
        // keeps the displayed cart in sync without relying on manual triggers.
        staleTime: 0,
        refetchOnWindowFocus: true,
        // Match prior behaviour, which never retried on its own.
        retry: false,
    });

    // Bridge to zustand so existing hasFreshData consumers don't need to change.
    useEffect(() => {
        if (status !== "authenticated") {
            setHasFreshData(false);
            return;
        }
        setHasFreshData(isSuccess);
    }, [isSuccess, status, setHasFreshData]);

    // Recalc only on first fetch per identity; running it on focus refetches
    // would bump the CT cart version and collide with concurrent tabs/devices.
    const recalculatedForIdentityRef = useRef<string | null>(null);
    useEffect(() => {
        if (!isSuccess) return;
        const identity = `${ucid ?? ""}|${anonymousId ?? ""}`;
        if (recalculatedForIdentityRef.current === identity) return;
        recalculatedForIdentityRef.current = identity;

        (async () => {
            try {
                const recalculateResponse = await recalculateCart();
                const expectedNoOpMessages = new Set([
                    "No active cart found.",
                    "No items in cart.",
                ]);
                const unexpectedErrors = recalculateResponse?.errors?.filter(
                    (err) => !expectedNoOpMessages.has(err?.message)
                );
                if (unexpectedErrors?.length) {
                    Logger.error(ServiceType.WEB, "Recalculating fresh cart failed", {
                        tag: LogTag.SHOPPING_BAG,
                        errors: unexpectedErrors,
                    });
                }
            } catch (error) {
                Logger.error(ServiceType.WEB, "Recalculating fresh cart threw", {
                    tag: LogTag.SHOPPING_BAG,
                    error,
                });
            }
        })();
    }, [isSuccess, ucid, anonymousId, recalculateCart]);
};
